import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, ShieldCheck, Clock, BadgeDollarSign, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { useReveal } from "@/lib/animations";
import { GoogleRating } from "@/components/GoogleRating";
import { CONTACT, PRODUCTS } from "@/lib/data";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPECT = [
  { icon: Clock, text: "A response within 24 hours — usually much faster" },
  { icon: ShieldCheck, text: "A licensed broker, not a call center" },
  { icon: BadgeDollarSign, text: "100% free, zero-pressure consultation" },
];

const inputCls =
  "w-full rounded-full bg-white/60 backdrop-blur border border-[#0B132B]/10 px-6 py-3.5 text-[#0B132B] placeholder:text-[#334155]/50 font-light outline-none transition-all duration-200 focus:border-[#29ABE2] focus:ring-2 focus:ring-[#29ABE2]/20";

export default function Quote() {
  const ref = useRef(null);
  useReveal(ref);
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    product_interest: params.get("product") || "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.product_interest) {
      toast.error("Please fill in your name, email, phone, and product interest.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Quote request received! We'll reach out within 24 hours.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        Array.isArray(detail) ? "Please double-check your email address." : "Something went wrong. Please try again or call us."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main ref={ref} data-testid="quote-page" className="bg-[#F0EBE0] grain min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-36 md:pt-44 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
            Get a quote
          </p>
          <h1
            data-reveal
            className="font-heading font-black text-5xl sm:text-6xl tracking-tighter leading-[0.95] text-[#0B132B]"
          >
            Your free quote, <span className="text-gradient-breeze">minutes away</span>.
          </h1>
          <p data-reveal className="mt-6 text-[#334155] font-light leading-relaxed">
            Tell us a little about yourself and what you're interested in. A
            licensed broker will build options from 50+ A-rated carriers —
            tailored to your family and your budget.
          </p>
          <div data-reveal className="mt-6">
            <GoogleRating />
          </div>

          <ul className="mt-10 space-y-5">
            {EXPECT.map((x, i) => {
              const Icon = x.icon;
              return (
                <li key={i} data-reveal data-reveal-delay={i * 0.08} className="flex items-center gap-4">
                  <span className="w-11 h-11 rounded-full bg-[#29ABE2]/10 text-[#0a6fc2] flex items-center justify-center shrink-0">
                    <Icon size={19} />
                  </span>
                  <span className="text-[#334155] font-light">{x.text}</span>
                </li>
              );
            })}
          </ul>

          <div data-reveal className="mt-12 rounded-3xl bg-[#0B132B] text-white p-7">
            <p className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
              Prefer to talk now?
            </p>
            <a
              href={CONTACT.phoneHref}
              data-testid="quote-phone-link"
              className="flex items-center gap-3 hover:text-[#29ABE2] transition-colors"
            >
              <Phone size={16} className="text-[#29ABE2]" /> {CONTACT.phone}
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="quote-email-link"
              className="mt-3 flex items-center gap-3 hover:text-[#29ABE2] transition-colors break-all"
            >
              <Mail size={16} className="text-[#29ABE2] shrink-0" /> {CONTACT.email}
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div
            data-reveal
            className="rounded-[2rem] bg-white/70 backdrop-blur-xl border border-white shadow-xl shadow-[#0B132B]/5 p-8 sm:p-12"
          >
            {done ? (
              <div data-testid="quote-success-message" className="text-center py-16">
                <CheckCircle2 size={56} className="mx-auto text-[#29ABE2]" />
                <h2 className="mt-6 font-heading font-black text-3xl tracking-tight text-[#0B132B]">
                  Request received!
                </h2>
                <p className="mt-4 text-[#334155] font-light max-w-sm mx-auto">
                  Thanks, {form.name.split(" ")[0]}. A licensed broker will reach
                  out within 24 hours with your personalized options.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-[#0B132B] mb-2 ml-2">Full name</label>
                    <input
                      data-testid="quote-name-input"
                      className={inputCls}
                      placeholder="Jane Doe"
                      value={form.name}
                      onChange={set("name")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B132B] mb-2 ml-2">Email</label>
                    <input
                      data-testid="quote-email-input"
                      type="email"
                      className={inputCls}
                      placeholder="jane@email.com"
                      value={form.email}
                      onChange={set("email")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B132B] mb-2 ml-2">Phone</label>
                    <input
                      data-testid="quote-phone-input"
                      type="tel"
                      className={inputCls}
                      placeholder="(555) 123-4567"
                      value={form.phone}
                      onChange={set("phone")}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-[#0B132B] mb-2 ml-2">
                      What are you interested in?
                    </label>
                    <Select
                      value={form.product_interest}
                      onValueChange={(v) => setForm({ ...form, product_interest: v })}
                    >
                      <SelectTrigger
                        data-testid="quote-product-select"
                        className="w-full rounded-full bg-white/60 backdrop-blur border border-[#0B132B]/10 px-6 h-[52px] text-[#0B132B] font-light focus:ring-2 focus:ring-[#29ABE2]/20 focus:border-[#29ABE2]"
                      >
                        <SelectValue placeholder="Choose a product" />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        {PRODUCTS.map((p) => (
                          <SelectItem
                            key={p.id}
                            value={p.name}
                            data-testid={`quote-product-option-${p.id}`}
                          >
                            {p.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="Not sure yet" data-testid="quote-product-option-unsure">
                          Not sure yet — help me decide
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-[#0B132B] mb-2 ml-2">
                      Anything else? <span className="text-[#334155]/50 font-light">(optional)</span>
                    </label>
                    <textarea
                      data-testid="quote-message-input"
                      rows={4}
                      className="w-full rounded-3xl bg-white/60 backdrop-blur border border-[#0B132B]/10 px-6 py-4 text-[#0B132B] placeholder:text-[#334155]/50 font-light outline-none transition-all duration-200 focus:border-[#29ABE2] focus:ring-2 focus:ring-[#29ABE2]/20 resize-none"
                      placeholder="Tell us about your goals, family, or current coverage..."
                      value={form.message}
                      onChange={set("message")}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit-button"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0B132B] text-white font-semibold px-8 py-4 transition-all duration-300 hover:bg-[#29ABE2] hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Get my free quote <ArrowRight size={17} />
                    </>
                  )}
                </button>
                <p className="mt-5 text-sm text-[#334155]/70 text-center font-light">
                  No spam, ever. Your information stays with Breeze Financial Group.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
